// Every word the News websites tab shows, in one place for the content team
// to review. `x` is the selected exchange's code, e.g. "NGX".

export const HOW_IT_WORKS = x =>
  `We read these websites regularly for new articles. When two of them report the same story, or one official source does, it's written in English and French and published. Switch a website off here to stop using it for ${x}.`;

export const HOW_UNPLACED =
  "These articles came from websites that feed more than one exchange, and we couldn't tell which one they're about. Tell us once, and we'll learn for next time.";

// §9.1 The list
export const LIST = {
  exchange: 'Exchange',
  exchangeTip:
    "The stock exchange you're working on. Everything on this page is for that exchange only.",
  pickExchange: 'Pick an exchange to see the news websites that feed it.',
  websites: n => `${n} ${n === 1 ? 'website' : 'websites'}`,
  working: n => `${n} working`,
  trouble: n => `${n} having trouble`,
  thisWeekSummary: n => `${n} articles this week`,
  thisWeekSummaryTip:
    'New articles we read from these websites in the last 7 days.',
  unplaced: n => `${n} ${n === 1 ? 'article' : 'articles'} we couldn't place →`,
  unplacedTip: "Articles we couldn't match to one exchange. Open to sort them.",
  website: 'Website',
  websiteTip:
    'The news website we read. Click it to see its settings and latest articles.',
  search: 'Search websites',
  searchTip: "Search by the website's name or address.",
  emptySearch: 'No websites match your search.',
  status: 'Status',
  statusTip:
    "Working: reading fine. Having trouble: the last read failed and we'll keep trying. Paused: we've stopped reading it.",
  lastChecked: 'Last checked',
  lastCheckedTip: 'When we last read this website without a problem.',
  thisWeek: 'This week',
  thisWeekTip:
    'New articles from this website in the last 7 days. Zero for several days usually means a problem.',
  alsoFeeds: 'Also feeds',
  alsoFeedsTip:
    "Other exchanges this website feeds. Changes here don't affect them.",
  activeOn: x => `Active on ${x}`,
  activeOnTip: x =>
    `Turn off to stop using this website for ${x}. It keeps feeding any other exchange.`,
  add: 'Add a website',
  addTip: x => `Add a news website for us to read for ${x}.`,
  more: 'More',
  removeFrom: x => `Remove from ${x}`,
  removeFromTip: x =>
    `Stop using this website for ${x}. It keeps feeding its other exchanges.`,
  delete: 'Delete website',
  deleteTip:
    'Remove this website everywhere. Stories already published stay live.',
  empty: x =>
    `No news websites for ${x} yet. Add the websites your team already reads for ${x} news. Start with the exchange's own website.`,
  noneYet: 'Not yet',
};

// Status words and their colours.
export const STATUS = {
  working: {label: 'Working', badge: 'success'},
  new: {label: 'New', badge: 'processing'},
  trouble: {label: 'Having trouble', badge: 'warning'},
  paused: {label: 'Paused', badge: 'default'},
  waiting: {label: 'Waiting for tech team', badge: 'default'},
};

export const NEW_SITE = "We'll read it for the first time within 15 minutes.";
export const WAITING_FOR_TECH =
  "We couldn't read this website automatically. The tech team needs to set it up.";
export const PAUSED_BY_EDITOR =
  'Someone paused this website. Resume it to start reading again.';

// §10 Websites: what went wrong, and the button that fixes it.
export const PROBLEMS = {
  BLOCKED: {
    message: 'This website is blocking us. Ask the tech team to check it.',
    action: 'Try again',
  },
  NOT_FOUND: {
    message:
      "The website's news feed has moved or been removed. Checking the website again usually finds the new one.",
    action: 'Check website again',
  },
  UNREACHABLE: {
    message: "The website didn't respond. We'll keep trying automatically.",
    action: 'Try again',
  },
  ROBOTS: {
    message:
      "This website asks automatic readers not to read it, so we can't use it.",
    action: null,
  },
  NO_ARTICLES: {
    message:
      'We can reach the website but found no new articles in 7 days. It may have stopped publishing.',
    action: 'Try again',
  },
  QUARANTINED: {
    message: last =>
      `We paused this website after it failed 5 times in a row. The last problem was: ${last}`,
    action: 'Try again',
  },
};

export const TRY_AGAIN_TIP =
  'Read the website now instead of waiting for the next scheduled read.';

// Confirmations say what will happen, in words.
export const CONFIRM = {
  removeTitle: (name, x) => `Remove ${name} from ${x}?`,
  remove: (name, x, others) =>
    `${name} will stop feeding ${x}. Its past articles stay.${
      others.length ? ` It will keep feeding ${others.join(' and ')}.` : ''
    }`,
  deleteTitle: name => `Delete ${name}?`,
  delete: (name, all) =>
    `${name} will be removed everywhere${
      all.length ? `: ${all.join(' and ')}` : ''
    }. Articles already published stay published.`,
  pauseTitle: name => `Pause ${name}?`,
  pause: name =>
    `We'll stop reading ${name} for every exchange until someone resumes it.`,
  offTitle: (name, x) => `Stop using ${name} for ${x}?`,
  off: (name, x) =>
    `${name} will stop feeding ${x}. It keeps feeding any other exchange.`,
};

export const TOAST = {
  added: (name, x) =>
    `${name} added to ${x}. We'll check it for new articles in the next 15 minutes.`,
  removed: (name, x) => `${name} no longer feeds ${x}.`,
  deleted: name => `${name} deleted.`,
  on: (name, x) => `${name} is active on ${x} again.`,
  off: (name, x) => `${name} no longer feeds ${x}.`,
  paused: name => `${name} paused.`,
  resumed: name => `${name} resumed. We'll read it within 15 minutes.`,
  retried: (name, n) =>
    n
      ? `Found ${n} new ${n === 1 ? 'article' : 'articles'} on ${name}.`
      : `Read ${name}. No new articles since last time.`,
  saved: name => `${name} saved.`,
  sentToTech: name =>
    `${name} is saved as Waiting for tech team. Let them know it needs setting up.`,
};

// §9.2 Add a website
export const ADD = {
  title: x => `Add a website to ${x}`,
  address: 'Website address',
  addressTip:
    "The website's main address, for example nairametrics.com. You don't need the full link.",
  addressPlaceholder: 'e.g. nairametrics.com',
  addressInvalid: 'Enter an address like nairametrics.com',
  check: 'Check website',
  checkTip:
    "Reads the website's latest articles, so you can see what we'd use before saving.",
  checking:
    'Checking the website and reading its latest articles… this can take up to 30 seconds.',
  canRead: name => `We can read ${name}. Here are its latest articles.`,
  alreadyElsewhere: (name, others, x) =>
    `${name} is already set up for ${others}. Add it to ${x} as well?`,
  alreadyHere: (name, x) => `${name} already feeds ${x}.`,
  openPage: 'Open its page',
  previouslyDeleted:
    'This website was deleted before. Adding it again starts fresh.',
  cantRead:
    "We can't read this website automatically. Some websites block automatic reading or don't publish a feed. The tech team can often set it up by hand.",
  fewArticles:
    'We found only a few articles for this website. Try another Google News edition under More options.',
  sendToTech: 'Send to tech team',
  sendToTechTip:
    "We can't read this website automatically. This asks the tech team to set it up.",
  back: 'Back',
  next: 'Next',
  preview: 'Its latest articles',
  previewTip:
    "What your answers below would do to this website's recent articles.",
  willUse: 'Will be used',
  willSkip: reason => `Will be skipped: ${reason}`,
  noSamples: 'The website had no recent articles to show.',
  name: 'Website name',
  nameTip: 'The name editors see for this website on these pages.',
  summaryTitle: 'Ready to add',
  save: 'Add website',
  saveTip: 'Saves the website. We start reading it within 15 minutes.',
  saveToExchange: x => `Add to ${x}`,
  moreOptions: 'More options',
  moreOptionsTip:
    'Settings most websites never need. The defaults suit this exchange.',
  language: 'Language',
  languageTip:
    'The language this website writes in. We translate its stories into the other language.',
  gnews: 'Google News edition',
  gnewsTip:
    'Only used if the website has no news feed. Keep the default; for Ivorian and other French-language websites, use France – French.',
  feedAddress: 'Feed address',
  feedAddressTip:
    'The address we read new articles from. Change it only if the website has moved its feed.',
  checkAgain: 'Check again',
  checkResults: 'What the check tried',
};

// The three questions (§4.2), also used when editing an exchange's settings.
export const QUESTIONS = {
  cover: 'What does this website cover?',
  coverTip:
    'How much of the website is relevant. Pick "All kinds of news" if it also covers politics, sport or entertainment.',
  coverMarket: x => `Only stock market news for ${x}`,
  coverAll: 'All kinds of news (politics, sport, business…)',
  coverSection: x => `Only one part of the website is about ${x}`,
  keywords: 'Only keep articles that mention:',
  keywordsTip:
    'We keep an article if its headline has one of these words, or its text has two different ones. Add words if good articles are skipped.',
  keywordsRule:
    'We keep an article when its headline has one of these words, or its text has two different ones.',
  keywordsAdd: 'Add a word and press Enter',
  keywordsNeeded: 'Add at least one word, or we would skip every article.',
  section: 'Which part of the website?',
  sectionTip: x =>
    `The section that covers ${x}. Articles from other sections are skipped.`,
  sectionOption: (host, section, n) =>
    `${host}${section}… (${n} ${n === 1 ? 'article' : 'articles'})`,
  sectionRequired:
    "This website is read through another website's feed, so choose the part that covers this exchange.",
  sectionNone:
    "We couldn't see separate sections in its recent articles. Pick another answer above, or ask the tech team.",
  reliability: 'How reliable is this website?',
  reliabilityTip:
    'A story needs two websites to publish, unless it comes from an official source. Most news websites are "Established".',
  official: 'Official source',
  officialHint:
    "The exchange itself, the regulator, or a listed company's investor page. One report is enough to publish.",
  officialTip:
    "The exchange, the regulator, or a company's own investor page. One report is enough to publish.",
  officialConfirmTitle: 'Treat it as an official source?',
  officialConfirm:
    'One report from this website will be enough to publish. Continue?',
  established: 'Established news organisation',
  establishedHint: 'Publishes once a second website reports the same story.',
  establishedTip:
    'A known news outlet. Its stories publish once another website reports them too.',
  smaller: 'Smaller or newer website',
  smallerHint:
    'Same rule, and its version of a story is used as the main source less often.',
  smallerTip:
    "Its stories publish once another website reports them too. We prefer the other website's version.",
};

export const SKIP_REASONS = {
  keyword: "doesn't mention the stock market",
  oneKeyword: 'mentions the market only once, in passing',
  section: 'outside the part of the site you chose',
};

// The Step 3 summary, written out.
export const SUMMARY = {
  feeds: (name, x) => `${name} will feed ${x}.`,
  market: "We'll use all of its articles.",
  keywords: words => `We'll use articles that mention ${words}.`,
  section: section => `We'll use articles from its ${section} section.`,
  official: 'One report from it is enough to publish a story.',
  established: 'It needs a second website to confirm a story.',
  smaller:
    "It needs a second website to confirm a story, and we'll usually prefer the other website's version.",
};

// §9.3 A website's page
export const PAGE = {
  back: 'Back to news websites',
  statusTip: 'When there is a problem, the yellow panel says what to do.',
  pause: 'Pause',
  pauseTip: 'Stop reading this website for every exchange.',
  resume: 'Resume',
  resumeTip: 'Start reading this website again for every exchange.',
  line: (checked, week) =>
    `last checked ${checked} · ${week} ${
      week === 1 ? 'article' : 'articles'
    } this week`,
  feeds: 'Feeds',
  feedsTip:
    'The exchanges this website feeds, and which of its articles each one uses.',
  edit: 'Edit',
  editTip: 'Change which articles we use from this website for this exchange.',
  editTitle: (name, x) => `${name} on ${x}`,
  feedAnother: 'Feed another exchange',
  feedAnotherTip:
    'Use this website for another exchange too, with its own settings.',
  feedAnotherTitle: name => `Use ${name} for another exchange`,
  whichExchange: 'Which exchange?',
  whichExchangeTip: 'The exchange this website should also feed.',
  active: 'Active',
  inactive: 'Not active',
  latest: 'Latest articles',
  latestTip:
    'The newest articles we read here, and whether each was used. Look here when a story seems to be missing.',
  usedTip:
    'Used: available for a story. Skipped: the reason says which setting left it out.',
  usedFor: x => `Used for ${x}`,
  unplaced: "Couldn't place",
  ignored: 'Ignored',
  skipped: reason => `Skipped: ${reason}`,
  latestNone: 'No articles read yet.',
  autoPublish: 'Publish without review',
  autoPublishTip:
    'Stories from this website go live without an editor checking them. Available once 20 of its drafts have been reviewed.',
  evidence: (unchanged, total, name) =>
    `${unchanged} of the last ${total} stories from ${name} were published without changes.`,
  evidenceTip:
    "How often editors published this website's drafts as written. A high number means it's safe to skip review.",
  notEnough: n => `Available after 20 reviewed drafts (${n} so far).`,
  autoPublishConfirmTitle: 'Publish without review?',
  autoPublishConfirm: (name, x) =>
    `Stories from ${name} will go live on ${x} without anyone checking them. Continue?`,
  loadingSamples: 'Reading its latest articles…',
  lastCheckedLabel: 'Last checked',
};

// One exchange's settings, as labelled rows on a website's page.
export const FEED = {
  uses: 'Articles we use',
  usesTip: "Which of this website's articles we use for this exchange.",
  usesAll: 'All of its articles',
  usesKeywords: 'Only ones that mention:',
  usesSection: section => `Only its ${section} section`,
  moreWords: n => `+${n} more`,
  reliability: 'Reliability',
  reliabilityTip:
    'A story needs two websites to publish, unless it comes from an official source.',
  reliabilityLabel: {
    official: 'Official source',
    established: 'Established news organisation',
    smaller: 'Smaller or newer website',
  },
  reviewsNotCounted: 'Available once editors have reviewed 20 of its drafts.',
};

// §9.3 Technical details
export const TECH = {
  howWeRead: 'How we read it',
  howWeReadTip:
    'How we get new articles: the news feed, the site map, the web pages, or Google News. Set by Check website.',
  modes: {
    AUTO: 'Chosen automatically',
    RSS: 'News feed',
    SITEMAP: 'Site map',
    HTML_LIST: 'Web pages',
    GNEWS: 'Google News',
  },
  feedAddress: ADD.feedAddress,
  feedAddressTip: ADD.feedAddressTip,
  gnews: ADD.gnews,
  gnewsTip: ADD.gnewsTip,
  interval: 'How often we read it (minutes)',
  intervalTip:
    'Minutes between reads. Lower means fresher news but more requests to the website.',
  perRead: 'Articles per read',
  perReadTip: 'The most new articles we take in one read.',
  robots: "Follow the website's rules for automatic readers",
  robotsTip: 'Leave on. Turning it off ignores what the website allows.',
  selectors: 'Page selectors',
  selectorsTip:
    'Where the headline, text and picture are on the page. Only needed when automatic reading fails.',
  // Shown only while Page selectors is on.
  selectorsHelp:
    'To find one: open an article from this website in Chrome, right-click the part you want (the headline, say), choose Inspect, then right-click the highlighted line and choose Copy › Copy selector. Paste it here.',
  selectorFields: {
    title: {
      label: 'Headline',
      tip: "Where the article's title is on its page. Usually the page's main heading.",
      placeholder: 'e.g. h1.entry-title',
    },
    content: {
      label: 'Text',
      tip: "The block that holds the article's paragraphs, without the menus, ads or comments around it.",
      placeholder: 'e.g. div.entry-content',
    },
    image: {
      label: 'Picture',
      tip: "The article's main picture. Leave empty to use the picture the website gives for sharing.",
      placeholder: 'e.g. figure.featured img',
    },
    date: {
      label: 'Date',
      tip: 'Where the date the article was published is shown.',
      placeholder: 'e.g. time.published',
    },
    author: {
      label: 'Author',
      tip: "Where the writer's name is shown. Optional.",
      placeholder: 'e.g. span.author-name',
    },
  },
  autoPublish: x => `Publish without review on ${x}`,
  autoPublishTip:
    "For the tech team: lets this website's stories go live without review before editors have reviewed 20 of its drafts.",
  lastError: 'Last error',
  lastErrorTip: 'The technical message from the last failed read.',
  save: 'Save technical details',
  saveTip: 'Saves the settings above.',
};

// §9.4 Articles we couldn't place
export const UNPLACED = {
  title: n =>
    `Articles we couldn't place · All exchanges · ${n} ${
      n === 1 ? 'article' : 'articles'
    }`,
  isNews: x => `This is ${x} news`,
  isNewsTip: x => `Use this article for ${x} stories.`,
  learn: x =>
    `Next time, automatically send articles that mention these words to ${x}`,
  learnGeneric:
    'Next time, automatically send articles that mention these words to the exchange I pick',
  learnTip:
    'Saves these words for this website, so similar articles go to the right exchange on their own.',
  learnPlaceholder: 'e.g. regional bourse',
  suggestions: 'Suggestions:',
  open: 'Open article ↗',
  openTip: 'Read the original on the website.',
  empty: 'All caught up. Every article has been placed.',
  placed: (x, word) =>
    word
      ? `Placed on ${x}. Articles that mention "${word}" will go there on their own.`
      : `Placed on ${x}.`,
};

// §4.3 Google News editions
export const GNEWS_EDITIONS = [
  {value: 'NG:en', label: 'Nigeria – English'},
  {value: 'FR:fr', label: 'France – French'},
  {value: 'US:en', label: 'United States – English'},
  {value: 'GB:en', label: 'United Kingdom – English'},
];

export const LANGUAGES = [
  {value: 'EN', label: 'English'},
  {value: 'FR', label: 'French'},
];
